import { useCallback, useEffect, useState } from "react";
import { authenticatedFetch, API_BASE, LIVE_API_BASE } from "./api";
import { API_ENDPOINTS } from "./apiEndpoints";
import "./Settings.css";

function newRunKey() {
  if (window.crypto.randomUUID) return window.crypto.randomUUID();
  const bytes = window.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64;
  bytes[8] = (bytes[8] & 63) | 128;
  const hex = Array.from(bytes, (byte) => byte.toString(16).padStart(2, "0")).join("");
  return `${hex.slice(0, 8)}-${hex.slice(8, 12)}-${hex.slice(12, 16)}-${hex.slice(16, 20)}-${hex.slice(20)}`;
}

async function responseData(response) {
  const data = await response.json();
  if (!response.ok) {
    throw new Error(typeof data.detail === "string" ? data.detail : "Unable to save API key settings.");
  }
  return data;
}

export default function Settings() {
  const [keys, setKeys] = useState([]);
  const [name, setName] = useState("Grok inventory workflow");
  const [stores, setStores] = useState(["tdo"]);
  const [created, setCreated] = useState(null);
  const [runKey, setRunKey] = useState(newRunKey);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState("");
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [revoking, setRevoking] = useState("");
  const [confirmRevoke, setConfirmRevoke] = useState("");
  const [denied, setDenied] = useState(false);

  const loadKeys = useCallback(async () => {
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.workflowKeySettings);
      setDenied(response.status === 403);
      const data = await responseData(response);
      setKeys(data.keys);
    } catch (err) {
      setError(err.message);
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => { loadKeys(); }, [loadKeys]);

  async function copy(value) {
    try {
      await navigator.clipboard.writeText(value);
      setNotice("Copied to clipboard.");
    } catch {
      setNotice("Clipboard is unavailable. Select the value and copy it manually.");
    }
  }

  async function createKey(event) {
    event.preventDefault();
    setCreating(true);
    setError("");
    setNotice("");
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.workflowKeySettings, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ name: name.trim(), stores }),
      });
      const data = await responseData(response);
      setCreated(data);
      await loadKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setCreating(false);
    }
  }

  async function revokeKey(id) {
    setRevoking(id);
    setError("");
    try {
      const response = await authenticatedFetch(API_ENDPOINTS.workflowKey(id), { method: "DELETE" });
      if (!response.ok) await responseData(response);
      if (created?.id === id) setCreated(null);
      setConfirmRevoke("");
      setNotice("API key revoked.");
      await loadKeys();
    } catch (err) {
      setError(err.message);
    } finally {
      setRevoking("");
    }
  }

  return (
    <main className="settings-page">
      <header><h1>Settings</h1><p>Manage access to your inventory workflows.</p></header>
      {error && <p role="alert" className="settings-error">{error}</p>}
      <p role="status" className="settings-notice">{notice}</p>

      <section className="settings-card" aria-labelledby="api-key-title">
        <h2 id="api-key-title">Workflow API keys</h2>
        <p>Create an API key here and give it to Grok in the <code>X-API-Key</code> header. The key stays the same across runs until you revoke it.</p>
        <form onSubmit={createKey}>
          <label htmlFor="workflow-key-name">Key name</label>
          <input id="workflow-key-name" value={name} onChange={(event) => setName(event.target.value)} required maxLength={100} disabled={denied || creating} />
          <fieldset disabled={denied || creating}>
            <legend>Stores this key can update</legend>
            {["tdo", "wdo", "kos"].map((store) => (
              <label key={store} className="settings-store"><input type="checkbox" checked={stores.includes(store)} onChange={() => setStores((current) => current.includes(store) ? current.filter((value) => value !== store) : [...current, store])} />{store.toUpperCase()}</label>
            ))}
          </fieldset>
          <button className="settings-primary" disabled={loading || creating || denied || !name.trim() || !stores.length || !!created} type="submit">{creating ? "Generating…" : "Generate API key"}</button>
        </form>

        {created && <div className="settings-secret">
          <h3>Your new API key</h3>
          <p>Copy it now. It will not be shown again after you leave or dismiss this panel.</p>
          <label htmlFor="new-workflow-api-key">X-API-Key</label>
          <textarea id="new-workflow-api-key" value={created.api_key} readOnly spellCheck={false} autoComplete="off" />
          <div className="settings-actions"><button type="button" onClick={() => copy(created.api_key)}>Copy API key</button><button type="button" onClick={() => setCreated(null)}>I've saved this key</button></div>
        </div>}

        {loading ? <p>Loading keys…</p> : !denied && <div className="settings-table-wrap"><table>
          <caption>Issued workflow API keys</caption>
          <thead><tr><th>Name</th><th>Stores</th><th>Status</th><th>Action</th></tr></thead>
          <tbody>{keys.length ? keys.map((key) => <tr key={key.id}>
            <td>{key.name}<small>{key.id}</small></td><td>{key.stores.map((store) => store.toUpperCase()).join(", ")}</td>
            <td>{key.revoked_at ? "Revoked" : "Active"}</td>
            <td>{!key.revoked_at && (confirmRevoke === key.id ? <div className="settings-actions"><button type="button" disabled={!!revoking} onClick={() => revokeKey(key.id)}>{revoking === key.id ? "Revoking…" : "Confirm revoke"}</button><button type="button" disabled={!!revoking} onClick={() => setConfirmRevoke("")}>Cancel</button></div> : <button type="button" disabled={!!revoking} onClick={() => setConfirmRevoke(key.id)}>Revoke</button>)}</td>
          </tr>) : <tr><td colSpan={4}>No API keys yet.</td></tr>}</tbody>
        </table></div>}
      </section>

      <section className="settings-card" aria-labelledby="run-key-title">
        <h2 id="run-key-title">Keys for individual runs</h2>
        <p>Grok can generate a fresh UUID for the <code>Idempotency-Key</code> on every new upload. You can also generate one here. Reuse the same value only when retrying the same upload.</p>
        <label htmlFor="workflow-run-key">Idempotency-Key</label>
        <input id="workflow-run-key" value={runKey} readOnly />
        <div className="settings-actions"><button type="button" onClick={() => copy(runKey)}>Copy run key</button><button type="button" onClick={() => { setRunKey(newRunKey()); setNotice("New run key generated."); }}>Generate new run key</button></div>
        <p className="settings-endpoint">Live workflow endpoint: <code>POST {LIVE_API_BASE}{API_ENDPOINTS.workflow.inventory}</code></p>
        {API_BASE !== LIVE_API_BASE && <p className="settings-endpoint">You are testing against <code>{API_BASE}</code>. Keys generated here work on this backend; generate a key on the live backend for live runs.</p>}
      </section>
    </main>
  );
}
